import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FaCheck,
  FaTimes,
  FaUserGraduate,
  FaMoneyBillWave,
  FaCalendarAlt,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function AssignFeeForm({ onClose, onSuccess }) {
  // Default to YYYY-MM format for HTML5 month picker
  const currentMonthYear = new Date().toISOString().slice(0, 7);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      studentId: "",
      feeStructureId: "",
      month: currentMonthYear,
    },
  });

  const { token } = useAuth();
  const [students, setStudents] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [studentsRes, feesRes] = await Promise.all([
          axios.get(`${server}/students`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${server}/fee-structures`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setStudents(studentsRes.data.data);
        setFeeStructures(feesRes.data.data);
      } catch (error) {
        toast.error("Error loading form data");
      }
    };
    if (token) fetchDropdownData();
  }, [token]);

  const onSubmit = async (data) => {
    try {
      // Format YYYY-MM string to "Month Year" string format e.g., "July 2026"
      let formattedMonth = data.month;
      if (data.month && data.month.includes("-")) {
        const [year, month] = data.month.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        formattedMonth = date.toLocaleString("default", {
          month: "long",
          year: "numeric",
        });
      }

      const payload = {
        studentId: parseInt(data.studentId),
        feeStructureId: parseInt(data.feeStructureId),
        month: formattedMonth,
      };

      await axios.post(`${server}/student-fees/assign`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Fee successfully assigned!");
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error assigning fee.");
      console.error(error);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl dark:shadow-2xl w-full max-w-lg mx-auto overflow-hidden border border-gray-100 dark:border-slate-800 transition-colors duration-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 dark:from-slate-800 dark:to-slate-900 p-6 flex justify-between items-center border-b border-transparent dark:border-slate-800">
        <h2 className="text-xl font-bold text-white flex items-center gap-3">
          <div className="p-2 bg-white/20 dark:bg-slate-700/50 rounded-lg">
            <FaUserGraduate className="text-white dark:text-emerald-400" />
          </div>
          Assign Student Fee
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-white/80 hover:text-white dark:text-slate-400 dark:hover:text-white transition-colors cursor-pointer"
        >
          <FaTimes size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
        {/* Select Student */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
            Select Student <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-3.5 top-3.5 text-gray-400 dark:text-slate-500 pointer-events-none">
              <FaUserGraduate />
            </div>
            <select
              {...register("studentId", { required: "Student is required" })}
              className={`w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-800/80 border ${
                errors.studentId
                  ? "border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-500/80"
                  : "border-gray-200 dark:border-slate-700 focus:border-emerald-500 dark:focus:border-emerald-400"
              } rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-100 dark:focus:ring-emerald-900/40 outline-none transition-all cursor-pointer`}
            >
              <option value="" className="dark:bg-slate-900">-- Choose Student --</option>
              {students.map((s) => (
                <option key={s.id} value={s.id} className="dark:bg-slate-900">
                  {s.firstName} {s.lastName} (Roll: {s.rollNumber})
                </option>
              ))}
            </select>
          </div>
          {errors.studentId && (
            <p className="text-xs text-red-500 mt-1 ml-1">
              {errors.studentId.message}
            </p>
          )}
        </div>

        {/* Fee Rule */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
            Fee Rule <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-3.5 top-3.5 text-gray-400 dark:text-slate-500 pointer-events-none">
              <FaMoneyBillWave />
            </div>
            <select
              {...register("feeStructureId", {
                required: "Fee Structure is required",
              })}
              className={`w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-800/80 border ${
                errors.feeStructureId
                  ? "border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-500/80"
                  : "border-gray-200 dark:border-slate-700 focus:border-emerald-500 dark:focus:border-emerald-400"
              } rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-100 dark:focus:ring-emerald-900/40 outline-none transition-all cursor-pointer`}
            >
              <option value="" className="dark:bg-slate-900">-- Choose Fee Rule --</option>
              {feeStructures.map((f) => (
                <option key={f.id} value={f.id} className="dark:bg-slate-900">
                  Class {f.class} - {f.title} (₹{f.amount})
                </option>
              ))}
            </select>
          </div>
          {errors.feeStructureId && (
            <p className="text-xs text-red-500 mt-1 ml-1">
              {errors.feeStructureId.message}
            </p>
          )}
        </div>

        {/* Billing Month (HTML5 Month Picker: Month + Year) */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
            Billing Month <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-3.5 top-3.5 text-gray-400 dark:text-slate-500 pointer-events-none">
              <FaCalendarAlt />
            </div>
            <input
              type="month"
              {...register("month", { required: "Billing Month is required" })}
              className={`w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-800/80 border ${
                errors.month
                  ? "border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-500/80"
                  : "border-gray-200 dark:border-slate-700 focus:border-emerald-500 dark:focus:border-emerald-400"
              } rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-100 dark:focus:ring-emerald-900/40 outline-none transition-all cursor-pointer`}
            />
          </div>
          {errors.month && (
            <p className="text-xs text-red-500 mt-1 ml-1">
              {errors.month.message}
            </p>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-semibold text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 rounded-xl shadow-lg shadow-emerald-200 dark:shadow-none disabled:opacity-70 disabled:shadow-none transition-all flex items-center gap-2 cursor-pointer"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : (
              <>
                <FaCheck /> Assign Fee
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
